import { useEffect, useState } from 'react';
import { logger } from '../../lib/core/env';

export function TranslationCompatible() {
  const [translationDetected, setTranslationDetected] = useState(false);
  
  useEffect(() => {
    // Apply Facebook React team monkey patches to prevent Google Translate DOM errors
    if (typeof Node === 'function' && Node.prototype) {
      const originalRemoveChild = Node.prototype.removeChild;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      Node.prototype.removeChild = function(child) {
        if (child.parentNode !== this) {
          if (console) {
            console.error('Cannot remove a child from a different parent', child, this);
          }
          return child;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line prefer-rest-params
        return originalRemoveChild.apply(this, arguments);
      };

      const originalInsertBefore = Node.prototype.insertBefore;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      Node.prototype.insertBefore = function(newNode, referenceNode) {
        if (referenceNode && referenceNode.parentNode !== this) {
          if (console) {
            console.error('Cannot insert before a reference node from a different parent', referenceNode, this);
          }
          return newNode;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line prefer-rest-params
        return originalInsertBefore.apply(this, arguments);
      };

      logger.info('DOM patches applied for Google Translate compatibility');
    }

    // Don't prevent translation, but detect it and handle gracefully
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) {
              // Detect Google Translate elements
              if (node.classList?.contains('VIpgJd-ZVi9od-ORHb-OEVmcd') || 
                  node.querySelector?.('.VIpgJd-ZVi9od-ORHb-OEVmcd') ||
                  node.getAttribute?.('data-google-translate-element')) {
                console.info('Translation detected - enabling compatibility mode');
                setTranslationDetected(true);
                
                // Add translation-active class to body for CSS adjustments
                document.body.classList.add('translation-active');
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Check if translation is already active
    const checkExistingTranslation = () => {
      const translateElements = document.querySelectorAll('.VIpgJd-ZVi9od-ORHb-OEVmcd, [data-google-translate-element]');
      if (translateElements.length > 0) {
        setTranslationDetected(true);
        document.body.classList.add('translation-active');
      }
    };

    // Check after a delay to catch already-translated content
    setTimeout(checkExistingTranslation, 1000);

    return () => {
      observer.disconnect();
      document.body.classList.remove('translation-active');
    };
  }, []);

  // If translation is detected, add some stability improvements
  useEffect(() => {
    if (translationDetected) {
      // Add some CSS to improve stability
      const style = document.createElement('style');
      style.textContent = `
        .translation-active {
          /* Reduce layout shifts during translation */
          transition: none !important;
        }
        
        .translation-active * {
          /* Prevent some animation conflicts */
          animation-play-state: paused;
        }
        
        /* Re-enable critical animations */
        .translation-active .animate-pulse,
        .translation-active .animate-ping,
        .translation-active .animate-spin {
          animation-play-state: running;
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        if (style.parentNode) {
          style.remove();
        }
      };
    }
  }, [translationDetected]);

  return null;
}

// Error recovery hook for translation-related errors
export function useTranslationErrorRecovery() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const error = event.error;
      
      // Check if it's a translation-related DOM error
      if (error instanceof Error && 
          (error.message.includes('insertBefore') || 
           error.message.includes('Node') ||
           error.message.includes('not a child'))) {
        
        console.warn('Translation-related DOM error detected, attempting recovery...');
        
        // Gentle recovery: try to re-render by forcing a small state change
        setTimeout(() => {
          const event = new CustomEvent('translation-recovery');
          window.dispatchEvent(event);
        }, 100);
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);
}